import { AxiosError, AxiosResponse } from 'axios'
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps, StaticContext } from 'react-router'
import { matchPath } from 'react-router-dom'

import { OrderDetail } from './OrderDetail'
import {
  checkCanOrderAgain,
  getUpdatedOrderState,
  getUpdatedOrderStateAfterDelete,
  getCarrierType,
  scrollToTrackingLinkKey
} from './OrderDetail.model'
import {
  AnnounceMode,
  IClinAnnounceBarProps
} from '../../../components/ClinAnnounceBar/ClinAnnounceBar'
import { isAusUser, OrderCancellationCodes } from '../../../constants'
import { useAppContext } from '../../../context/app'
import { addBasketItemAsync, useBasket } from '../../../context/basket'
import { createAnnounceEvent } from '../../../events/AnnounceEvent'
import {
  createConfirmationEvent,
  clearConfirmationEvent
} from '../../../events/ConfirmationEvent'
import { AddedToBasketConfirmation } from '../../../features/AddedToBasketConfirmation'
import { IBasketItemDetails } from '../../../features/AddedToBasketConfirmation/AddedToBasketConfirmation'
import { OrderLineCancellationModal } from '../../../features/OrderLineCancellationModal'
import { useEffectOnlyOnce } from '../../../hooks/useEffectOnlyOnce/useEffectOnlyOnce'
import { AnalyticsEvent } from '../../../services/Analytics'
import { AnalyticsPageEvent } from '../../../services/Analytics/AnalyticsPageEvent'
import analyticsServiceSingleton from '../../../services/Analytics/initAnalytics'
import {
  AuthError,
  cancelGetOrderById,
  cancelSetDocumentToHold,
  getOrderById,
  getPriceAndAvailabilityForSku,
  removeOrderLine,
  setDocumentToHold,
  upsertSkuItemToBasket,
  downloadOrderInvoice
} from '../../../services/ApiService'
import { getHoldDocumentForHold } from '../../../services/Holds/holds'
import { getVariantInformationForCatalogItem } from '../../../services/PortfolioJourneys/PortfolioJourney.model'
import { IAugmentedCatalogItemDto } from '../../../services/PortfolioJourneys/PortfolioJourney.types'
import { IError } from '../../../types'
import { findOrderCancellationLookupCode } from '../../../types/IOrder'
import { IRestEndpointState } from '../../../types/IRestEndpointState'
import { OrderStatus } from '../../../types/OrderStatus'
import {
  BasketDto,
  DeleteOrderDto,
  HoldDto,
  InstituteDto,
  OrderDto,
  OrderLineDto,
  UploadedDocumentDto
} from '../../../types/swaggerTypes'
import { handleBasketError, resetBasket } from '../../../utils/basketUtils'
import { useFeatureFlags } from '../../../context/featureFlags/FeatureFlagContext'
import { FeatureFlagKeys } from '../../../context/featureFlags/FeatureFlagKeys'
import useChangeBackgroundColor from '../../../hooks/useChangeBackgroundColor/useChangeBackgroundColor'
import { ClinTheme } from '../../../ClinTheme'
import { ClinDrawer } from '../../../components/ClinDrawer'
import { BasketDrawer } from '../../../features/BasketDrawer/BasketDrawer'
import { isOrderEligibleForInvoice } from '../../../utils/orderUtils'
import { CheckoutDrawer } from '../../../features/CheckoutDrawer/CheckoutDrawer'

type LocationState = {
  from: string
  orderDto?: OrderDto
  displayAnnounceOrderSuccess?: boolean
}

export interface IOrderDetailProps
  extends RouteComponentProps<{}, StaticContext, LocationState> {}

export const OrderDetailContainer: FunctionComponent<IOrderDetailProps> = ({
  history,
  location
}) => {
  const { t } = useTranslation()
  const { useFeatureFlag } = useFeatureFlags()
  const isNewCheckoutPage = useFeatureFlag(
    FeatureFlagKeys.NewOneStepCheckoutPage
  )
  const enableNewCheckoutDrawer = useFeatureFlag(
    FeatureFlagKeys.NewGaCheckoutDrawer
  )
  const enableNewBasketDrawer = useFeatureFlag(FeatureFlagKeys.NewBasketDrawer)
  const { orderDto, displayAnnounceOrderSuccess } = location.state
  const matchPatientDetails =
    history.location.state &&
    matchPath(history.location.state.from, {
      path: `/programs/my-physicians/:physicianId/:patientId`
    })

  const backButtonTitle = matchPatientDetails
    ? t('order_detail:back_to_patient_details_btn')
    : 'View orders'
  const {
    dispatch,
    defaultShippingAddress,
    portfolioCountryCode,
    institute,
    user,
    userDetails
  } = useAppContext()
  const [{ viewBasket }, basketDispatch] = useBasket()
  const [order, setOrder] = React.useState<OrderDto | undefined>(orderDto)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [isAddingToBasket, setAddingToBasket] = React.useState<boolean>(false)
  const [isDrawerLoading, setIsDrawerLoading] = React.useState<boolean>(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false)
  const [isCheckoutDrawerOpen, setIsCheckoutDrawerOpen] =
    useState<boolean>(false)
  const [isDownloadingInvoice, setIsDownloadingInvoice] =
    useState<boolean>(false)
  const [canAllItemsBeOrdered, setCanAllItemsBeOrdered] =
    React.useState<boolean>(() =>
      orderDto ? checkCanOrderAgain(orderDto, portfolioCountryCode) : false
    )
  const [orderAnnounce, setOrderAnnounce] = React.useState<
    IClinAnnounceBarProps | undefined
  >()
  const [orderLine, setOrderLine] = React.useState<OrderLineDto>()
  const [quantity, setQuantity] = React.useState<number>(0)
  const [unitPrice, setUnitPrice] = React.useState<number>(0)

  const [isCancelOrderlineModalOpen, setIsCancelOrderlineModalOpen] =
    React.useState<boolean>(false)
  const [selectedOrderLine, setSelectedOrderLine] = React.useState<
    OrderLineDto | undefined
  >()
  const [deleteOrderDescription, setDeleteOrderDescription] = React.useState<
    DeleteOrderDto | undefined
  >()
  const [cancellationSubmitting, setCancellationSubmitting] =
    React.useState<boolean>(false)
  const [isConfirmingOrderStatus, setIsConfirmingOrderStatus] =
    useState<boolean>(false)
  const pollOrderStatusTimeoutIdsRef = useRef<ReturnType<typeof setTimeout>[]>(
    []
  )

  const cancellationOptions =
    OrderCancellationCodes && Object.values(OrderCancellationCodes)
  const [cancellationHasError, setCancellationHasError] =
    React.useState<IError>()

  useChangeBackgroundColor(ClinTheme.colors.lightGrey)

  // CLOS-14614: the order arrives in route state, so without this the flag kept its
  // initial value until a line was cancelled, and the order confirmation page offered
  // every item again whether or not it could be ordered.
  React.useEffect(() => {
    setCanAllItemsBeOrdered(
      order ? checkCanOrderAgain(order, portfolioCountryCode) : false
    )
  }, [order, portfolioCountryCode])

  const handleGetOrder = useCallback(() => {
    if (order) {
      setIsLoading(true)
      getOrderById(order.orderNumber)
        .then((response) => {
          setOrder(response.data)
        })
        .catch((error) => {
          // If request is cancelled continue
          if (error.message === AuthError.RequestCancelled) {
            return
          }
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `There was an error fetching the order. ${error}`
            )
          )
          analyticsServiceSingleton.trackError(
            error,
            JSON.parse(localStorage.getItem('current_user') || '{}')
          )
        })
        .finally(() => setIsLoading(false))
    }
  }, [dispatch, order, portfolioCountryCode])

  const handleBack = () => {
    if (
      (history.location.state && history.location.state.from === '/orders') ||
      matchPatientDetails
    ) {
      history.goBack()
    } else {
      history.push('/orders')
    }
  }
  const handleTrackingLinkClicked = (
    orderLine: OrderLineDto,
    currentLanguage: string
  ) => {
    analyticsServiceSingleton.trackEvent(AnalyticsEvent.DeliveryTracking, {
      carrierName: orderLine.trackingInfo?.freightCarrier,
      instituteId: user?.institute?.instituteId,
      language: currentLanguage,
      location: history.location.pathname
    })
  }

  const handleCancelOrderLine = (orderLine: OrderLineDto) => {
    const updateOrderLineDto: DeleteOrderDto = {
      cancelReason: '',
      cancelComments: ''
    }
    setDeleteOrderDescription(updateOrderLineDto)
    setIsCancelOrderlineModalOpen(true)
    setSelectedOrderLine(orderLine)
  }

  const handleCancellationOptionChange = (id: string) => {
    if (deleteOrderDescription) {
      const updateDeleteOrderDesc: DeleteOrderDto = {
        ...deleteOrderDescription,
        cancelReason: id ? findOrderCancellationLookupCode[id] : ''
      }
      setDeleteOrderDescription(updateDeleteOrderDesc)
    }
  }

  const handleCancellationTextChange = (comment: string) => {
    if (deleteOrderDescription) {
      const updateOrderLineDto: DeleteOrderDto = {
        ...deleteOrderDescription,
        cancelComments: comment
      }
      setDeleteOrderDescription(updateOrderLineDto)
    }
  }

  const handleSubmitCancellation = () => {
    if (
      order &&
      deleteOrderDescription &&
      selectedOrderLine &&
      selectedOrderLine?.lineId
    ) {
      setCancellationSubmitting(true)
      removeOrderLine(
        order.orderNumber,
        selectedOrderLine?.lineId.toString(),
        deleteOrderDescription
      )
        .then((response) => {
          if (response.status === 200) {
            handleGetOrder()
            setOrderAnnounce({
              title: ' ',
              message: t('order_detail:successful_cancellation'),
              mode: AnnounceMode.Success,
              isDismissible: true
            })
            setIsCancelOrderlineModalOpen(false)
            selectedOrderLine.lineId &&
              analyticsServiceSingleton.trackEvent(
                AnalyticsEvent.CancelOrderedItem,
                {
                  orderId: order.orderNumber,
                  itemId: selectedOrderLine.lineId.toString()
                }
              )
            if (response.data.lines.length === 0) {
              history.push('/orders')
            }
            if (
              response.data.lines.filter(
                (item) => item.status === OrderStatus.Cancelled
              ).length === response.data.lines.length
            ) {
              history.push('/orders')
            }
          }
        })
        .catch((error) => {
          // If request is cancelled continue
          if (error.message === AuthError.RequestCancelled) {
            return
          }

          setCancellationHasError({
            hasError: true,
            errorMessage: `${t('order_detail:error_cancelling')} ${error}`
          })
          analyticsServiceSingleton.trackError(
            error,
            JSON.parse(localStorage.getItem('current_user') || '{}')
          )
        })
        .finally(() => {
          setCancellationSubmitting(false)
          setIsLoading(false)
        })
    }
  }

  const handleCancellationClose = () => {
    setIsCancelOrderlineModalOpen(false)
    setDeleteOrderDescription(undefined)
  }

  const handleEmailOrderDetails = () => {
    // Validate order status
    if (!isOrderEligibleForInvoice(order)) {
      setOrderAnnounce({
        message: t('order_detail:error_downloading_invoice'),
        mode: AnnounceMode.Warning,
        isDismissible: true
      })
      return
    }

    if (!order?.orderNumber) {
      return
    }

    setIsDownloadingInvoice(true)

    downloadOrderInvoice(order.orderNumber)
      .then((response) => {
        if (response.status === 200) {
          // Create a URL for the blob
          const blob = new Blob([response.data], { type: 'application/pdf' })
          const downloadUrl = window.URL.createObjectURL(blob)

          // Create a temporary link and click it to download
          const a = document.createElement('a')
          a.href = downloadUrl
          a.download = `invoice_${order.orderNumber}.pdf`
          document.body.appendChild(a)
          a.click()

          // Clean up
          window.URL.revokeObjectURL(downloadUrl)
          document.body.removeChild(a)

          setOrderAnnounce({
            title: ' ',
            message: t('order_detail:invoice_downloaded'),
            mode: AnnounceMode.Success,
            isDismissible: true
          })

          analyticsServiceSingleton.trackEvent(AnalyticsEvent.DownloadInvoice, {
            orderNumber: order.orderNumber,
            orderStatus: order.orderStatus,
            deliverToContact: order.deliverToContact,
            shipMethod: order.shipMethod,
            shipToContact: order.shipToContact,
            type: 'GA'
          })
        }
      })
      .catch((error: AxiosError) => {
        // Enhanced error handling
        let errorMessage = t('order_detail:error_downloading_invoice')
        if (error.response?.status === 404) {
          errorMessage = t('order_detail:error_downloading_invoice')
        } else if (error.response?.status === 403) {
          errorMessage = t('order_detail:error_downloading_invoice')
        } else if (error.response?.status === 500) {
          errorMessage = t('order_detail:error_downloading_invoice')
        }

        setOrderAnnounce({
          message: `${errorMessage} ${error}`,
          mode: AnnounceMode.Error,
          isDismissible: true
        })

        analyticsServiceSingleton.trackEvent(
          AnalyticsEvent.DownloadInvoiceError,
          {
            orderNumber: order.orderNumber,
            orderStatus: order.orderStatus,
            deliverToContact: order.deliverToContact,
            shipMethod: order.shipMethod,
            shipToContact: order.shipToContact,
            type: 'GA'
          }
        )
      })
      .finally(() => {
        setIsDownloadingInvoice(false)
      })
  }

  const handleAddToBasket = async (
    orderLine: OrderLineDto,
    quantity: number
  ) => {
    setQuantity(quantity)
    setUnitPrice(orderLine.unitSellingPrice)
    setOrderLine(orderLine)
    const sku = orderLine?.skuCatalogItem?.sku
    // Reset the basket if it's checking out
    await resetBasket(dispatch, basketDispatch)
    if (sku && defaultShippingAddress) {
      setIsDrawerLoading(true)
      getPriceAndAvailabilityForSku(sku, defaultShippingAddress.addressId)
        .then((response) => {
          addBasketItemAsync(basketDispatch, sku, quantity, response.data)
            .then(() => {
              setAddingToBasket(true)
            })
            .catch((error) => {
              setAddingToBasket(false)
              handleBasketError(error, undefined, handleAddToBasket)
            })
            .finally(() => {
              setIsDrawerLoading(false)
              window.scrollTo({
                behavior: 'smooth',
                top: 0
              })
            })
        })
        .catch((error) => {
          setAddingToBasket(false)
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `${t('order_detail:error_adding_item_to_basket')} ${error}`
            )
          )
        })
    }
  }

  const handleRequestAgain = async () => {
    if (order && defaultShippingAddress) {
      // Reset the basket if it's checking out
      await resetBasket(dispatch, basketDispatch)

      const skus: string[] = []
      order.lines.forEach((o) => {
        const variantInformation: IAugmentedCatalogItemDto | null =
          o.skuCatalogItem &&
          getVariantInformationForCatalogItem(
            o.skuCatalogItem,
            portfolioCountryCode
          )
        const cannotReOrder = variantInformation?.cannotOrder
        if (
          o.skuCatalogItem?.sku &&
          o.medicationFlag === 'Y' &&
          !cannotReOrder
        ) {
          skus.push(o.skuCatalogItem.sku)
        }
      })
      if (skus.length > 0) {
        const updateBasketCalls: Promise<AxiosResponse<BasketDto>>[] = skus.map(
          (sku) => upsertSkuItemToBasket(sku, 1)
        )
        Promise.all(updateBasketCalls)
          .then(() => {
            // Prices etc. will be updated in basket load
            setTimeout(() => {
              history.push('/basket')
            }, 500)
          })
          .catch((error) => {
            dispatch(
              createAnnounceEvent(
                AnnounceMode.Error,
                `${t('order_detail:error_reordering')} ${error}`
              )
            )
            window.scrollTo(0, 0)
          })
          .finally(() => setAddingToBasket(false))
      }
      analyticsServiceSingleton.trackEvent(AnalyticsEvent.OpenRepeatOrderModal)
    }
  }

  const handleGoToBasket = () => {
    history.push('/basket')
  }

  const handleProceedToCheckout = () => {
    if (isAusUser(portfolioCountryCode)) {
      history.push('/one-step-checkout')
    } else if (enableNewCheckoutDrawer) {
      setIsCheckoutDrawerOpen && setIsCheckoutDrawerOpen(true)
    } else {
      history.push('/checkout')
    }
  }

  const handleProceedToCheckoutOld = () => {
    history.push('/basket')
  }

  const handleFileUploadedForOrderAndHold = (
    orderId: string,
    orderLine: OrderLineDto,
    hold: HoldDto,
    document: UploadedDocumentDto
  ): Promise<void> => {
    const holdDocumentType: string | undefined = getHoldDocumentForHold(hold)
    if (hold.holdId && holdDocumentType) {
      return setDocumentToHold(
        orderId,
        document.uploadedDocumentId,
        orderLine.lineId,
        hold,
        holdDocumentType
      )
        .then((response) => {
          // Update order with document details
          if (order) {
            setOrder(
              getUpdatedOrderState(
                order,
                orderLine,
                hold,
                holdDocumentType,
                document,
                response?.data?.documentId,
                response?.data?.dataPlatformDocumentId
              )
            )
          }
        })
        .catch((error) => {
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `${t('order_detail:error_uploading_document')} ${error}`
            )
          )
          // Trigger refresh to remove document
          window.scrollTo(0, 0)
          throw error
        })
    } else {
      console.warn(
        'No holdId or holdDocumentType supplied to setDocumentToHold'
      )
      return Promise.reject(
        new Error('No holdId or holdDocumentType supplied to setDocumentToHold')
      )
    }
  }

  const handleFileDeleteForOrderAndHold = (
    orderLine: OrderLineDto,
    hold: HoldDto,
    documentId?: number,
    dataPlatformDocumentId?: string
  ) => {
    if (
      hold.holdId &&
      (documentId !== undefined || Boolean(dataPlatformDocumentId))
    ) {
      if (order) {
        setOrder(
          getUpdatedOrderStateAfterDelete(
            order,
            orderLine,
            hold,
            documentId,
            dataPlatformDocumentId
          )
        )
      }
    } else {
      console.warn(
        'No holdId or documentId is provided when deleting a document.'
      )
    }
  }

  const removeLocalStorageInformation = () => {
    if (
      userDetails?.contactCard.name &&
      localStorage.getItem(userDetails?.contactCard.name)
    ) {
      localStorage.removeItem(userDetails?.contactCard.name)
    }
  }

  useEffect(
    () => {
      if (!orderLine || !isAddingToBasket) {
        return
      }

      if (orderLine?.skuCatalogItem) {
        const basketItemDetails: IBasketItemDetails = {
          title: orderLine.skuCatalogItem.item.genericConcatenation,
          sku: orderLine.skuCatalogItem.sku,
          quantity: quantity,
          amount: quantity * unitPrice,
          currencyCode: viewBasket?.currencyCode
            ? viewBasket?.currencyCode
            : 'GBP',
          requiresDocumentation: false // TODO: Work out whether this needs to be true or false
        }
        setAddingToBasket(false)

        if (enableNewBasketDrawer) {
          setIsDrawerOpen && setIsDrawerOpen(true)
        } else {
          dispatch(
            createConfirmationEvent({
              children: (
                <AddedToBasketConfirmation
                  basketItemDetails={basketItemDetails}
                  handleGoToBasket={handleGoToBasket}
                  handleProceedToCheckout={
                    isNewCheckoutPage
                      ? handleProceedToCheckout
                      : handleProceedToCheckoutOld
                  }
                  wasItemRemoved={false}
                  handleClose={() => dispatch(clearConfirmationEvent())}
                />
              ),
              autoHideAfterMs: 10 * 1000,
              boxWidth: 360,
              borderRadius: 8,
              position: 'absolute',
              topPosition: -75,
              removePadding: true
            })
          )
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [viewBasket, setIsDrawerOpen, isAddingToBasket, quantity, unitPrice]
  )

  // cancel api calls on unmount
  useEffect(() => {
    window.addEventListener('beforeunload', removeLocalStorageInformation)
    return () => {
      //this is for removing data from local storage that are preventing fast track new users to see new feature on order details page
      removeLocalStorageInformation()
      window.removeEventListener('beforeunload', removeLocalStorageInformation)
      cancelGetOrderById()
      cancelSetDocumentToHold()
      pollOrderStatusTimeoutIdsRef.current.forEach(clearTimeout)
    }
  }, [])

  useEffect(() => {
    // Scroll to the middle of the page (or any target element with the corresponding ID)
    const shouldScrollToTrackingLink = localStorage.getItem(
      scrollToTrackingLinkKey
    )

    const trackingLink = document.getElementById('tracking-link')

    if (shouldScrollToTrackingLink === 'True' && trackingLink) {
      trackingLink.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
      localStorage.removeItem(scrollToTrackingLinkKey)
    }
  }, [])

  useEffect(() => {
    if (!displayAnnounceOrderSuccess) {
      return
    }

    setOrderAnnounce({
      title: ' ',
      message: t('order_detail:order_successfully_submitted'),
      mode: AnnounceMode.Success,
      isDismissibleText: true
    })

    history.replace(location.pathname, {
      ...location.state,
      displayAnnounceOrderSuccess: false
    })

    // Oracle may still be evaluating the order for holds when this page first
    // renders. Poll briefly for a line status change so a hold that lands
    // moments after submission (e.g. IHA) shows its upload control without
    // requiring a manual refresh.
    const initialLineStatuses = order?.lines
      .map((line) => line.status)
      .join('|')

    const pollOrderStatus = (isLastPoll: boolean) => {
      if (!order) {
        return
      }
      getOrderById(order.orderNumber)
        .then((response) => {
          setOrder(response.data)
          const updatedLineStatuses = response.data.lines
            .map((line) => line.status)
            .join('|')
          if (updatedLineStatuses !== initialLineStatuses) {
            pollOrderStatusTimeoutIdsRef.current.forEach(clearTimeout)
            setIsConfirmingOrderStatus(false)
          } else if (isLastPoll) {
            // No hold appeared: stop confirming as soon as the last poll is back.
            setIsConfirmingOrderStatus(false)
          }
        })
        .catch((error) => {
          // If request is cancelled continue
          if (error.message === AuthError.RequestCancelled) {
            return
          }
          if (isLastPoll) {
            setIsConfirmingOrderStatus(false)
          }
          analyticsServiceSingleton.trackError(
            error,
            JSON.parse(localStorage.getItem('current_user') || '{}')
          )
        })
    }

    setIsConfirmingOrderStatus(true)
    const pollDelaysMs = [3000, 6000, 12000]
    pollOrderStatusTimeoutIdsRef.current = pollDelaysMs.map((delay, index) =>
      setTimeout(
        () => pollOrderStatus(index === pollDelaysMs.length - 1),
        delay
      )
    )
    // Safety net in case the last poll hangs: never confirm for longer than 20s.
    pollOrderStatusTimeoutIdsRef.current.push(
      setTimeout(() => setIsConfirmingOrderStatus(false), 20000)
    )
  }, [])

  type Deps = [OrderDto, IRestEndpointState<InstituteDto>, string]
  useEffectOnlyOnce(
    (dependencies: Deps) => {
      analyticsServiceSingleton.trackPageView(AnalyticsPageEvent.ViewOrder, {
        'Order number': dependencies[0].orderNumber,
        Institution: dependencies[1].data?.instituteName
      })
    },
    [order, institute],
    (dependencies: Deps) => dependencies[0] && dependencies[1]
  )

  const listOfNewFeatures = undefined //expired feature

  return (
    <>
      <OrderLineCancellationModal
        orderLine={selectedOrderLine}
        isOpen={isCancelOrderlineModalOpen}
        selectedOption={
          deleteOrderDescription &&
          findOrderCancellationLookupCode[deleteOrderDescription?.cancelReason]
        }
        cancellationOptions={cancellationOptions}
        isSubmitting={cancellationSubmitting}
        handleCommentChange={handleCancellationTextChange}
        handleOptionChange={handleCancellationOptionChange}
        handleSubmit={handleSubmitCancellation}
        handleClose={handleCancellationClose}
        hasError={cancellationHasError && cancellationHasError.hasError}
        errorMessage={cancellationHasError && cancellationHasError.errorMessage}
      />
      {!isLoading && (
        <OrderDetail
          order={order}
          newFeaturesList={listOfNewFeatures}
          isLoading={isLoading}
          countryCode={portfolioCountryCode}
          isAddingToBasket={isAddingToBasket}
          isDrawerLoading={isDrawerLoading}
          isDownloadingInvoice={isDownloadingInvoice}
          isConfirmingOrderStatus={isConfirmingOrderStatus}
          orderAnnounce={orderAnnounce}
          backButtonTitle={backButtonTitle}
          handleDismissAnnounce={() => setOrderAnnounce(undefined)}
          onPrint={() => window.print()}
          canAllItemsBeOrdered={canAllItemsBeOrdered}
          onRequestAgain={handleRequestAgain}
          onBack={handleBack}
          onCancelOrderLine={handleCancelOrderLine}
          onAddToBasket={handleAddToBasket}
          onEmailOrderDetails={handleEmailOrderDetails}
          handleFileUploadedForOrderAndHold={handleFileUploadedForOrderAndHold}
          handleFileDeleteForOrderAndHold={handleFileDeleteForOrderAndHold}
          handleTrackingLinkClicked={handleTrackingLinkClicked}
          getCarrierType={getCarrierType}
        />
      )}
      {enableNewBasketDrawer && (
        <ClinDrawer
          open={isDrawerOpen}
          width={512}
          onClose={() => setIsDrawerOpen(false)}
        >
          <BasketDrawer
            handleClose={() => setIsDrawerOpen(false)}
            handleCheckoutOpen={() => setIsCheckoutDrawerOpen(true)}
          />
        </ClinDrawer>
      )}
      {enableNewCheckoutDrawer && (
        <ClinDrawer
          open={isCheckoutDrawerOpen}
          width={512}
          onClose={() => setIsCheckoutDrawerOpen(false)}
        >
          <CheckoutDrawer handleClose={() => setIsCheckoutDrawerOpen(false)} />
        </ClinDrawer>
      )}
    </>
  )
}
